import { Logo } from '@/components/logo';
import { useCurrentUser } from '@/hooks';
import { Avatar, Flex, Menu, Text } from '@mantine/core';
import {
	IconArrowsLeftRight,
	IconBuildingFactory,
	IconHomeBolt,
	IconLayoutGridAdd,
	IconLogout,
	IconMessageCircle,
	IconPhoto,
	IconProps,
	IconRestore,
	IconSettings,
	IconShoppingCart,
	IconTrophy,
	IconUser,
	IconUserBolt,
} from '@tabler/icons-react';
import { Link, useLocation, useNavigate } from '@tanstack/react-router';
import { useCallback, useMemo } from 'react';
import { RoleName } from '../../../../../backend/src/types.generated';
import styles from './styles.module.css';

const navItems = [
	{
		label: 'Home',
		to: '/home',
		icon: IconHomeBolt,
	},
	{
		label: 'Scan',
		to: '/scan',
		icon: IconLayoutGridAdd,
	},
	{
		label: 'Rewards',
		to: '/rewards',
		icon: IconTrophy,
	},
	{
		label: 'Order',
		to: '/shop',
		icon: IconShoppingCart,
	},
];

const facilityNavItems = [
	{
		label: 'Home',
		to: '/facility/home',
		icon: IconHomeBolt,
	},
	{
		label: 'History',
		to: '/facility/history',
		icon: IconRestore,
	},
	{
		label: 'Log Out',
		to: '/auth/logout',
		icon: IconLogout,
	},
	{
		label: 'Settings',
		to: '/facility/settings',
		icon: IconSettings,
	},
];

export const DesktopNav = ({ context = 'home' }: { context?: 'home' | 'facility' }) => {
	return (
		<Flex direction="column" align="center" justify="center" className={styles.desktopNavRoot}>
			<Flex px="xl" direction="row" align="center" justify="space-between" w="100%">
				<Link to="/">
					<Logo />
				</Link>
				<Flex direction="row" align="center">
					{context !== 'facility' ? (
						<>
							{navItems.map((item) => (
								<NavItem key={item.label} {...item} />
							))}
						</>
					) : (
						<>
							{facilityNavItems.map((item) => (
								<NavItem key={item.label} {...item} />
							))}
						</>
					)}
					{context !== 'facility' && (
						<>
							<div className={styles.divider} />
							<ProfileDropdown />
						</>
					)}
				</Flex>
			</Flex>
		</Flex>
	);
};

const NavItem = ({
	icon: Icon,
	label,
	to,
}: {
	icon: React.FunctionComponent<IconProps>;
	label: string;
	to: string;
}) => {
	const navigate = useNavigate();
	const { pathname } = useLocation();

	const active = useMemo(() => {
		if (to === '/home') return pathname === '/home';
		if (to === '/scan') {
			switch (pathname) {
				case '/scan':
					return true;
				case '/enter-code':
					return true;
				case '/scan-code':
					return true;
				default:
					return false;
			}
		}
		if (to === '/rewards') {
			switch (pathname) {
				case '/rewards':
					return true;
				case '/past-winners':
					return true;
				default:
					return false;
			}
		}
		if (to === '/shop') {
			switch (pathname) {
				case '/shop':
					return true;
				case '/checkout-product':
					return true;
				default:
					return false;
			}
		}
		return false;
	}, [pathname, to]);

	return (
		<Flex
			direction="row"
			gap="xs"
			align="center"
			h={70}
			onClick={() => navigate({ to })}
			px="md"
			style={{
				borderTop: active ? '6px solid var(--mantine-color-green-7)' : '6px solid transparent',
				color: active ? 'var(--mantine-color-green-7)' : undefined,
				cursor: 'pointer',
			}}
		>
			<Icon size={28} />
			<Text size="sm" fw={600}>
				{label}
			</Text>
		</Flex>
	);
};

const ProfileDropdown = () => {
	const { currentUser, logUserOut } = useCurrentUser();
	const navigate = useNavigate();
	const { pathname } = useLocation();
	const contextSwitchOption = useMemo(() => {
		const currentContext = pathname.includes('/facility') ? 'Facility' : 'User';
		const isAdmin = Boolean(
			currentUser?.roles?.some((role) => role.name === RoleName.Admin) ||
				currentUser?.roles?.some((role) => role.name === RoleName.SuperAdmin)
		);
		let canSwitch = false;
		if (
			currentContext === 'User' &&
			Boolean(currentUser?.roles?.some((role) => role.name === RoleName.Facility) || isAdmin)
		) {
			canSwitch = true;
		} else if (
			currentContext === 'Facility' &&
			Boolean(currentUser?.roles?.some((role) => role.name === RoleName.User) || isAdmin)
		) {
			canSwitch = true;
		}

		if (canSwitch) {
			return currentContext === 'Facility' ? 'User' : 'Facility';
		}
		return null;
	}, [currentUser, pathname]);
	const logout = useCallback(async () => {
		await logUserOut();
		navigate({ to: '/welcome' });
	}, [logUserOut, navigate]);
	return (
		<Menu shadow="md" width={200}>
			<Menu.Target>
				<Flex className={styles.clickable} direction="row" gap="sm" align="center">
					<Avatar>
						<IconUserBolt size={22} color="black" />
					</Avatar>
					<Text fw={700} size="sm">
						{currentUser?.contact?.firstName}
					</Text>
				</Flex>
			</Menu.Target>

			<Menu.Dropdown>
				<Menu.Label>Application</Menu.Label>
				<Menu.Item
					onClick={() => navigate({ to: '/account' })}
					leftSection={<IconSettings size={14} />}
				>
					My Profile
				</Menu.Item>
				<Menu.Item
					onClick={() => navigate({ to: '/scan-history' })}
					leftSection={<IconMessageCircle size={14} />}
				>
					Scan History
				</Menu.Item>
				<Menu.Item
					onClick={() => navigate({ to: '/alerts' })}
					leftSection={<IconPhoto size={14} />}
				>
					Alerts
				</Menu.Item>
				{contextSwitchOption && (
					<Menu.Item
						onClick={() =>
							navigate({ to: contextSwitchOption === 'Facility' ? '/facility/home' : '/home' })
						}
						leftSection={
							contextSwitchOption === 'Facility' ? (
								<IconBuildingFactory size={14} />
							) : (
								<IconUser size={14} />
							)
						}
					>
						Switch to {contextSwitchOption}
					</Menu.Item>
				)}

				<Menu.Divider />
				<Menu.Item onClick={logout} leftSection={<IconArrowsLeftRight size={14} />}>
					Log Out
				</Menu.Item>
			</Menu.Dropdown>
		</Menu>
	);
};
